import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useContext, useEffect, useRef, useState } from "react";
import { MATERIAL_ORDER_STATUS, ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { api } from "../../../../shared/hooks/api";
import { color } from "../../../../shared/styles";
import UserIcon from "../../../../shared/svg/UserIcon";
import { error, success } from "../../../../utils/toast";
import { InfoBoxTitle, InfoBoxWrapper } from "../Styled";
import { AssigneeBox, DropdownMenuContent, DropdownMenuItem, DropdownMenuItemTitle, DropdownMenuItemUnassigned, DropdownMenuSeparator } from "./Styled";

// Mapping of role keys to display names
const ROLE_DISPLAY: Record<string, string> = {
    manager: "Quản lý hậu cần",
    purchasing_staff: "Nhân viên hậu cần mua vật tư",
    first_storage_keeper: "Thủ kho 1",
    second_storage_keeper: "Thủ kho 2",
    logistic_manager: "Quản lý logistics",
};

const Content = ({
    onChangeValue,
    assignee,
    title,
    role,
    locked = false,
    isUpdating = false,
}: {
    onChangeValue: Function;
    assignee: any;
    title: string;
    role?: string;
    locked?: boolean;
    isUpdating?: boolean;
}) => {
    const [{ data, isLoading }] = api.get(
        `/users/?role=${role?.toUpperCase()}`
    );
    
    const triggerRef = useRef<HTMLDivElement>(null);
    const [menuWidth, setMenuWidth] = useState<number>(260);
    useEffect(() => {
        const updateWidth = () => {
            const w = triggerRef.current?.offsetWidth || 260;
            setMenuWidth(Math.max(w, 260));
        };
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, [assignee]);

    const {
        user: { role: currentRole },
    } = useContext(AuthenticationContext);

    const canEdit = (toEditRole?: string) => {
        switch (toEditRole) {
            case "purchasing_staff":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF].includes(
                    currentRole
                );
             case "first_storage_keeper":
             case "second_storage_keeper":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF].includes(
                    currentRole
                );
            case "manager":
                return [ROLE.MANAGER].includes(currentRole);
            case "logistic_manager":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF].includes(currentRole);
            default:
                return false;
        }
    };

    // Render read-only (locked) view without dropdown when locked
    if (locked) {
        return (
            <AssigneeBox>
                <div className="left">
                    <div className="title">{title}</div>
                    <div className="content locked" style={{ cursor: "default" }}>
                        {isUpdating ? (
                            "Đang cập nhật..."
                        ) : assignee ? (
                            <span className="assignee-item">
                                <span className="assignee-icon"><UserIcon /></span>
                                <span className="assignee-text">
                                    <span className="assignee-name">{assignee?.display_name || assignee?.username || "Không rõ tên"}</span>
                                    {assignee?.email ? (
                                        <span className="assignee-email">{assignee.email}</span>
                                    ) : null}
                                </span>
                            </span>
                        ) : ("Chưa bàn giao")}
                    </div>
                </div>
            </AssigneeBox>
        )
    }

    return (
        <AssigneeBox>
            <DropdownMenu.Root>
                <div className="left">
                    <div className="title">{title}</div>
                    <DropdownMenu.Trigger asChild>
                        <div ref={triggerRef} className="content" style={{cursor: canEdit(role) ? "pointer" : "default"}}>
                            {assignee ? (
                                <span className="assignee-item">
                                    <span className="assignee-icon"><UserIcon /></span>
                                    <span className="assignee-text">
                                        <span className="assignee-name">{assignee?.display_name || assignee?.username || "Không rõ tên"}</span>
                                        {assignee?.email ? (
                                            <span className="assignee-email">{assignee.email}</span>
                                        ) : null}
                                    </span>
                                </span>
                            ) : ("Chưa bàn giao")}
                        </div>
                    </DropdownMenu.Trigger>
                </div>

                <DropdownMenu.Portal>
                    <DropdownMenuContent
                        side="bottom"
                        align="start"
                        sideOffset={6}
                        style={{
                            minWidth: menuWidth,
                            width: menuWidth,
                        }}
                    >
                        <DropdownMenuItemTitle disabled>
                            {isLoading ? "Đang tải..." : `Chọn ${ROLE_DISPLAY[role || ""] || "người phụ trách"}`}
                        </DropdownMenuItemTitle>
                        <DropdownMenuSeparator />
                        <DropdownMenuItemUnassigned
                            onSelect={() => onChangeValue(undefined)}
                            style={{
                                padding: "8px 10px",
                                fontSize: 14,
                                cursor: "pointer",
                                borderRadius: 6,
                                fontFamily: "Noto Sans",
                                color: color.secondary.CHARCOAL,
                            }}
                        >
                            Bỏ chọn
                        </DropdownMenuItemUnassigned>
                        {(data?.results || []).map((user: any) => (
                            <DropdownMenuItem key={user.id} onSelect={() => onChangeValue(user)}>
                                <span>{user.display_name || user.username}</span>
                                {assignee?.id === user.id ? (
                                    <span style={{ color: color.primary.BLUE_B100 }}>✓</span>
                                ) : null}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </AssigneeBox>
    );
};

interface AssigneeField {
    title: string;
    fieldName: string;
    role?: string;
}

export const Assignees = ({
    data: materialOrder,
    updateData,
    fetchData,
    mergeMaterialOrder,
    fields,
}: {
    data: any;
    updateData: Function;
    fetchData?: any;
    mergeMaterialOrder?: Function;
    fields: AssigneeField[];
    disabledEdit?: boolean;
}) => {
    const [updatingField, setUpdatingField] = useState<string | null>(null);

    const onChangeAssignee = (field: string, role?: string) => async (assignee: any) => {
        if (updatingField) return;
        setUpdatingField(field);
        try {
            const current = materialOrder[role || ""] || null;

            // If no assignee selected and no current assignee → no change
            if (!assignee) {
                if (!current) {
                    success("THÀNH CÔNG", `${ROLE_DISPLAY[role || ""] || "Nhân sự"} - không có thay đổi`);
                    return;
                }
            }
            
            // If the selected user is already assigned → no change
            if (assignee?.id && current && current["id"] === assignee.id) {
                success("THÀNH CÔNG", `${ROLE_DISPLAY[role || ""] || "Nhân sự"} - không có thay đổi`);
                return;
            }

            const updatePayload = assignee?.id
                ? { [field]: assignee?.id }
                : { [field]: "" };

            await updateData(updatePayload);

            // Optimistically update local state to avoid full refresh and flicker
            if (mergeMaterialOrder) {
                const localUpdatePayload = assignee
                    ? { [field]: assignee }
                    : { [field]: null };
                mergeMaterialOrder(localUpdatePayload);
            } else if (fetchData) {
                // Fallback to refectch if mergeMaterialOrder is not provided
                await fetchData();
            }

            const roleText = ROLE_DISPLAY[role || ""] || "Nhân sự";
            const actionText = assignee?.id ? "Bàn giao" : "Bỏ chọn";
            const nameText = assignee?.display_name || assignee?.username || "";

            success(
                "THÀNH CÔNG",
                `${actionText}${nameText ? ` - ${nameText}` : ""}${roleText ? ` (${roleText})` : ""} thành công`,
            );
        } catch (err) {
            const roleText = ROLE_DISPLAY[role || ""] || "Nhân sự";
            error(`${roleText} - cập nhật thất bại`);
        } finally {
            setUpdatingField(null);
        }
    };

    return (
        <InfoBoxWrapper>
            <InfoBoxTitle>Phụ trách</InfoBoxTitle>
            {fields.map((item) => {
                const currentStatus = String(materialOrder?.status);
                const r = (item.role || "").toLowerCase();
                const isLocked = (() => {
                    switch (currentStatus) {
                        case MATERIAL_ORDER_STATUS.IMPORTING:
                        case MATERIAL_ORDER_STATUS.RESTOCK:
                            return true;
                        default:
                            return false;
                    }
                })();

                return (
                    <Content
                        key={item.fieldName}
                        title={item.title}
                        onChangeValue={onChangeAssignee(item.fieldName, item.role)}
                        assignee={(materialOrder as any)[item.fieldName as any]}
                        role={item.role}
                        locked={isLocked || updatingField !== null}
                        isUpdating={updatingField === item.fieldName}
                    />
                );
            })}
        </InfoBoxWrapper>
    );
};
